import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import {
  useGiftCodeOrderLookup,
  useIssueGiftCode,
} from "@babackapp/private-api-client/client/use-issue-gift-code";
import { downloadGiftCode } from "@babackapp/private-api-client/client/use-download-gift-code";
import type { GiftCodeOrderLookupDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";
import { Button, ButtonLoading } from "@babackapp/ui/components/button";
import { Checkbox } from "@babackapp/ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@babackapp/ui/components/radio-group";
import { GiftIcon } from "lucide-react";
import { FormEvent, useState } from "react";

type ApiError = { code?: string } | undefined;

function lookupErrorKey(error: ApiError) {
  if (error?.code === "ORDER_NOT_FOUND")
    return "giftCodes.generate.orderNotFound";
  if (error?.code === "GIFT_CODES_NOT_ACTIVATED")
    return "giftCodes.generate.notActivated";
  return "giftCodes.generate.lookupError";
}

/**
 * Back-office generation of a gift code for one line item (BBS-376). No email
 * leaves unless the merchant ticks "Send by email" and gives a recipient.
 */
export default function GenerateGiftCodeDialog() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const lookup = useGiftCodeOrderLookup();
  const issue = useIssueGiftCode();

  const [isOpen, setIsOpen] = useState(false);
  const [orderName, setOrderName] = useState("");
  const [order, setOrder] = useState<GiftCodeOrderLookupDto | null>(null);
  const [lineItemId, setLineItemId] = useState<string>("");
  const [shouldSendEmail, setShouldSendEmail] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");

  const selectedLineItem = order?.lineItems.find(
    (lineItem) => lineItem.lineItemId === lineItemId,
  );
  const canSubmit =
    !!selectedLineItem && (!shouldSendEmail || recipientEmail.trim() !== "");

  const reset = () => {
    setOrderName("");
    setOrder(null);
    setLineItemId("");
    setShouldSendEmail(false);
    setRecipientEmail("");
    lookup.reset();
    issue.reset();
  };

  const onOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) reset();
  };

  const onSearch = async (event: FormEvent) => {
    event.preventDefault();
    if (!orderName.trim()) return;

    setOrder(null);
    setLineItemId("");
    const result = await lookup
      .mutateAsync({
        params: { path: { shopId: shop.id }, query: { orderName } },
      })
      .catch(() => null);
    if (!result) return;

    setOrder(result);
    setRecipientEmail(result.customerEmail ?? "");
    if (result.lineItems.length === 1) {
      setLineItemId(result.lineItems[0].lineItemId);
    }
  };

  const onSubmit = async () => {
    if (!order || !selectedLineItem) return;

    // Opened synchronously, on the click: a window opened after the request
    // returns is no longer tied to the click and gets blocked as a popup.
    const pdfWindow = window.open("", "_blank");
    const result = await issue
      .mutateAsync({
        params: { path: { shopId: shop.id } },
        body: {
          orderId: order.orderId,
          lineItemId: selectedLineItem.lineItemId,
          recipientEmail: shouldSendEmail ? recipientEmail.trim() : undefined,
        },
      })
      .catch(() => null);

    if (!result) {
      pdfWindow?.close();
      return;
    }

    if (pdfWindow) pdfWindow.location.href = result.url;
    else window.open(result.url, "_blank");
    onOpenChange(false);
  };

  const onDownloadExisting = async (giftCodeId: string) => {
    const pdfWindow = window.open("", "_blank");
    const { url } = await downloadGiftCode({ giftCodeId, shopId: shop.id });
    if (pdfWindow) pdfWindow.location.href = url;
    else window.open(url, "_blank");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <GiftIcon className="size-4" />
          {t("giftCodes.generate.button")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("giftCodes.generate.title")}</DialogTitle>
          <DialogDescription>
            {t("giftCodes.generate.description")}
          </DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-2" onSubmit={onSearch}>
          <Label htmlFor="gift-code-order-name">
            {t("giftCodes.generate.orderLabel")}
          </Label>
          <div className="flex gap-2">
            <Input
              id="gift-code-order-name"
              placeholder="#1001"
              value={orderName}
              onChange={(event) => setOrderName(event.target.value)}
              autoFocus
            />
            <ButtonLoading
              type="submit"
              variant="outline"
              isLoading={lookup.isPending}
              disabled={!orderName.trim()}
            >
              {t("giftCodes.generate.search")}
            </ButtonLoading>
          </div>
          {lookup.isError && (
            <p className="text-sm text-destructive">
              {t(lookupErrorKey(lookup.error as ApiError))}
            </p>
          )}
        </form>

        {order && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label>
                {t("giftCodes.generate.itemLabel")} · {order.orderName}
              </Label>
              <RadioGroup value={lineItemId} onValueChange={setLineItemId}>
                {order.lineItems.map((lineItem) => (
                  <div
                    key={lineItem.lineItemId}
                    className="flex items-center gap-3 rounded-md border p-2"
                  >
                    <RadioGroupItem
                      value={lineItem.lineItemId}
                      id={`gift-code-line-${lineItem.lineItemId}`}
                    />
                    {lineItem.image && (
                      <img
                        src={lineItem.image}
                        alt=""
                        className="size-10 rounded object-cover"
                      />
                    )}
                    {/* Lines of one product only differ by their variant:
                        without it, five "Blazer" rows look identical. */}
                    <Label
                      htmlFor={`gift-code-line-${lineItem.lineItemId}`}
                      className="flex flex-1 flex-col gap-0.5 font-normal"
                    >
                      <span>
                        {lineItem.title}
                        {lineItem.variantTitle && (
                          <span className="font-medium">
                            {" "}
                            — {lineItem.variantTitle}
                          </span>
                        )}
                        <span className="text-muted-foreground">
                          {" "}
                          ·{" "}
                          {t("giftCodes.generate.quantity", {
                            quantity: lineItem.quantity,
                          })}
                        </span>
                      </span>
                      {lineItem.sku && (
                        <span className="text-xs text-muted-foreground">
                          {lineItem.sku}
                        </span>
                      )}
                    </Label>
                    {lineItem.giftCode && (
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">
                          {t("giftCodes.generate.existingCode", {
                            code: lineItem.giftCode.giftCode,
                          })}
                        </Badge>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            onDownloadExisting(lineItem.giftCode!.id)
                          }
                        >
                          {t("giftCodes.generate.downloadExisting")}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="gift-code-send-email"
                  checked={shouldSendEmail}
                  onCheckedChange={(checked) =>
                    setShouldSendEmail(checked === true)
                  }
                />
                <Label htmlFor="gift-code-send-email" className="font-normal">
                  {t("giftCodes.generate.sendEmail")}
                </Label>
              </div>
              {shouldSendEmail && (
                <Input
                  type="email"
                  aria-label={t("giftCodes.generate.recipientLabel")}
                  placeholder={t("giftCodes.generate.recipientLabel")}
                  value={recipientEmail}
                  onChange={(event) => setRecipientEmail(event.target.value)}
                />
              )}
            </div>

            {issue.isError && (
              <p className="text-sm text-destructive">
                {t("giftCodes.generate.issueError")}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <ButtonLoading
            onClick={onSubmit}
            isLoading={issue.isPending}
            disabled={!canSubmit}
          >
            {selectedLineItem?.giftCode
              ? t("giftCodes.generate.submitExisting")
              : t("giftCodes.generate.submit")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
